import { IconSparkles, IconX } from '@tabler/icons-react';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { authClient } from '@/auth/client';
import { trackRetentionEvent } from '@/analytics/retention-events';
import { CreditPackagesDialog } from '@/components/pricing/public-credit-packages';
import { Button } from '@/components/ui/button';
import { useCredits } from '@/hooks/use-credits';
import { useCurrentPlan } from '@/hooks/use-payment';
import { getAllCreditPackages } from '@/credits/packages';
import { getRecommendedCreditPackage } from '@/lib/credit-top-up';
import { cn } from '@/lib/utils';

export const POST_GENERATION_CREDIT_OFFER_EVENT =
  'grok:post-generation-credit-offer';

const OFFER_DELAY_MS = 8000;
const OFFER_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const OFFER_SESSION_KEY = 'post_generation_credit_offer_seen_session';
const OFFER_DISMISSED_UNTIL_KEY =
  'post_generation_credit_offer_dismissed_until';

type PostGenerationCreditOfferKind = 'image' | 'video';

interface PostGenerationCreditOfferDetail {
  actionCost: number;
  balance?: number;
  kind: PostGenerationCreditOfferKind;
  model?: string;
  resultId?: string;
  tool?: string;
  returnTo?: string;
  onBeforeCheckout?: () => void | Promise<void>;
}

type PostGenerationCreditOfferEvent =
  CustomEvent<PostGenerationCreditOfferDetail>;

export function showPostGenerationCreditOffer(
  detail: PostGenerationCreditOfferDetail
) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(POST_GENERATION_CREDIT_OFFER_EVENT, { detail })
  );
}

export function PostGenerationCreditOfferDialog() {
  const locationHref =
    useRouterState({ select: (state) => state.location.href }) ?? '';
  const { data: session, isPending: isSessionPending } =
    authClient.useSession();
  const creditsQuery = useCredits();
  const currentPlanQuery = useCurrentPlan(Boolean(session?.user));
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<PostGenerationCreditOfferDetail>({
    actionCost: 0,
    kind: 'image',
  });
  const [packagesOpen, setPackagesOpen] = useState(false);
  const timerRef = useRef<number | null>(null);
  const offerLocationRef = useRef(locationHref);
  const shownResultIdsRef = useRef(new Set<string>());
  const userId = session?.user?.id;
  const monetizationTier = currentPlanQuery.data?.monetizationTier ?? 'free';
  const balance = detail.balance ?? creditsQuery.data?.balance;
  const hasInsufficientBalance =
    typeof balance === 'number' && balance < detail.actionCost;
  const recommendedPackage = useMemo(
    () =>
      getRecommendedCreditPackage(getAllCreditPackages(), {
        actionCost: detail.actionCost,
        balance: balance ?? 0,
      }),
    [balance, detail.actionCost]
  );
  const checkoutMetadata = useMemo(() => {
    const metadata: Record<string, string> = {
      source: 'post_generation_light_offer',
      kind: detail.kind,
      requiredCredits: String(detail.actionCost),
      resumeAction: 'variation',
      monetizationTier,
    };
    if (recommendedPackage) metadata.packageId = recommendedPackage.id;
    if (detail.tool) metadata.tool = detail.tool;
    if (detail.model) metadata.model = detail.model;
    if (detail.resultId) metadata.resultId = detail.resultId;
    if (typeof balance === 'number') metadata.balance = String(balance);
    return metadata;
  }, [
    balance,
    detail.actionCost,
    detail.kind,
    detail.model,
    detail.resultId,
    detail.tool,
    monetizationTier,
    recommendedPackage,
  ]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (offerLocationRef.current === locationHref) return;
    offerLocationRef.current = locationHref;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setOpen(false);
    setPackagesOpen(false);
  }, [locationHref]);

  useEffect(() => {
    function handleOffer(event: Event) {
      const customEvent = event as PostGenerationCreditOfferEvent;
      const eventDetail = customEvent.detail;
      if (!eventDetail) return;
      const nextDetail = {
        ...eventDetail,
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      };
      if (
        nextDetail.resultId &&
        shownResultIdsRef.current.has(nextDetail.resultId)
      ) {
        return;
      }
      setDetail(nextDetail);
      offerLocationRef.current = locationHref;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        if (nextDetail.resultId) {
          shownResultIdsRef.current.add(nextDetail.resultId);
        }
        setOpen(true);
      }, OFFER_DELAY_MS);
    }

    window.addEventListener(POST_GENERATION_CREDIT_OFFER_EVENT, handleOffer);
    return () =>
      window.removeEventListener(
        POST_GENERATION_CREDIT_OFFER_EVENT,
        handleOffer
      );
  }, [locationHref]);

  useEffect(() => {
    if (
      !open ||
      isSessionPending ||
      creditsQuery.isLoading ||
      currentPlanQuery.isPending
    ) {
      return;
    }
    if (
      !userId ||
      !hasInsufficientBalance ||
      !recommendedPackage ||
      (detail.kind === 'video' && monetizationTier === 'free')
    ) {
      setOpen(false);
      return;
    }
    if (hasSessionSeenOffer() || isOfferDismissed()) {
      setOpen(false);
      return;
    }
    markSessionSeenOffer();
    trackRetentionEvent('topup_offer_view', {
      actionCost: detail.actionCost,
      balance,
      kind: detail.kind,
      packageId: recommendedPackage.id,
    });
  }, [
    balance,
    detail.actionCost,
    detail.kind,
    hasInsufficientBalance,
    creditsQuery.isLoading,
    currentPlanQuery.isPending,
    isSessionPending,
    monetizationTier,
    open,
    recommendedPackage,
    userId,
  ]);

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) markOfferDismissed();
  }

  if (!open || !recommendedPackage) return null;

  return (
    <aside
      aria-labelledby="post-generation-credit-offer-title"
      aria-describedby="post-generation-credit-offer-description"
      className={cn(
        'fixed right-4 bottom-4 z-50 w-[calc(100vw-2rem)] max-w-[26rem] overflow-hidden rounded-2xl border border-[#d4af37]/25 bg-[#080808] text-white shadow-[0_28px_90px_rgba(0,0,0,0.56)] sm:right-6 sm:bottom-6'
      )}
      role="dialog"
    >
      <button
        type="button"
        aria-label="Not now"
        onClick={() => handleOpenChange(false)}
        className="absolute top-3 right-3 z-10 inline-flex size-8 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-white/62 transition hover:bg-white/12 hover:text-white"
      >
        <IconX className="size-4" />
      </button>
      <div className="border-[#d4af37]/18 border-b bg-[#15120a] px-5 pt-5 pb-4">
        <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/12 px-3 py-1 font-semibold text-[#ffe9a8] text-xs">
          <IconSparkles className="size-3.5" />
          Result ready
        </div>
        <h2
          id="post-generation-credit-offer-title"
          className="pr-8 font-semibold text-xl text-white leading-tight"
        >
          {monetizationTier === 'credit_buyer'
            ? 'You are low on credits. Top up to keep creating.'
            : 'Your result is ready. Want to keep creating?'}
        </h2>
        <p
          id="post-generation-credit-offer-description"
          className="pt-2 text-sm text-white/62 leading-6"
        >
          {detail.kind === 'video'
            ? 'Use credits to edit this video, extend the scene, upscale it, or create another version.'
            : 'Use credits to edit this image, regenerate a stronger version, or turn it into a Grok video.'}
        </p>
      </div>
      <div className="space-y-3 px-5 py-4">
        <Button
          type="button"
          className="h-11 w-full rounded-xl bg-[#d4af37] font-semibold text-black shadow-[0_14px_36px_rgba(212,175,55,0.24)] transition hover:bg-[#f1d36b]"
          onClick={() => setPackagesOpen(true)}
        >
          View yearly and full credit plans
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-10 w-full rounded-xl text-white/72 hover:bg-white/[0.08] hover:text-white"
          onClick={() => handleOpenChange(false)}
        >
          Keep editing this result
        </Button>
        <button
          type="button"
          className="block w-full text-center font-medium text-white/42 text-xs transition hover:text-white/68"
          onClick={() => handleOpenChange(false)}
        >
          Not now
        </button>
      </div>
      <CreditPackagesDialog
        checkoutMetadata={checkoutMetadata}
        description="Choose the plan or credit pack that fits your next generation batch."
        onBeforeCheckout={async () => {
          await detail.onBeforeCheckout?.();
          trackRetentionEvent('topup_checkout_start', {
            actionCost: detail.actionCost,
            kind: detail.kind,
          });
        }}
        onOpenChange={setPackagesOpen}
        open={packagesOpen}
        returnTo={detail.returnTo}
        title="Keep creating with the plan that fits your workflow"
      />
    </aside>
  );
}

function hasSessionSeenOffer() {
  try {
    return sessionStorage.getItem(OFFER_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markSessionSeenOffer() {
  try {
    sessionStorage.setItem(OFFER_SESSION_KEY, '1');
  } catch {
    // Storage failures should not block the post-generation workflow.
  }
}

function isOfferDismissed() {
  try {
    return (
      Number(localStorage.getItem(OFFER_DISMISSED_UNTIL_KEY) ?? '0') >
      Date.now()
    );
  } catch {
    return false;
  }
}

function markOfferDismissed() {
  try {
    localStorage.setItem(
      OFFER_DISMISSED_UNTIL_KEY,
      String(Date.now() + OFFER_COOLDOWN_MS)
    );
  } catch {
    // Storage failures are non-fatal; checkout and editing remain available.
  }
}
